'use client'
import {
  context_default as context_default2
} from "./chunk-3EM7C2X3.mjs";
import {
  context_default
} from "./chunk-4HCJHTK4.mjs";
import {
  useWebsiteConfig
} from "./chunk-WYH7GL2G.mjs";
import {
  CalendarConfig
} from "./chunk-6XSQRWIQ.mjs";
import {
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-BTG5EALA.mjs";

// src/context/root/provider.tsx
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

// src/context/config/provider.tsx
import { useRouter as useRouter2 } from "@wbk/config";

// src/context/favorites/provider.tsx
import { useMemo, useRef, useState } from "react";
import { CloseIcon } from "@wbk/svg";
import { Button, Toast, Typography } from "@wbk/ui";
import { Link, Trans, useRouter, useTranslation } from "@wbk/config";
import { jsx, jsxs } from "react/jsx-runtime";
var FavoritesProvider = ({ children }) => {
  const { t } = useTranslation("calendar_common");
  const { locale, pathname } = useRouter();
  const [favorites, setFavorites] = useState(() => {
    const storedFavorites = JSON.parse(localStorage.getItem("favorites") || "[]");
    return storedFavorites;
  });
  const initialSlugs = useMemo(
    () => favorites,
    // eslint-disable-next-line
    [pathname]
  );
  const toastRef = useRef(null);
  const timeoutRef = useRef({});
  const updateFavorites = (newFavorites) => {
    setFavorites(newFavorites);
    localStorage.setItem("favorites", JSON.stringify(newFavorites));
  };
  const removeFavoriteWithUndo = (slug, title) => {
    var _a;
    const updatedFavorites = favorites.filter((fav) => fav !== slug);
    updateFavorites(updatedFavorites);
    (_a = toastRef.current) == null ? void 0 : _a.publish((to) => ({
      title: /* @__PURE__ */ jsxs("div", { className: "bg-body flex items-center gap-2 rounded-md p-4", children: [
        /* @__PURE__ */ jsx("div", { className: "flex-1", children: /* @__PURE__ */ jsxs(
          Trans,
          {
            i18nKey: "favorites.event_removed_from_favorites",
            ns: "calendar_common",
            values: { title },
            children: [
              /* @__PURE__ */ jsx(Typography, { className: "inline", strong: true }),
              /* @__PURE__ */ jsx(
                Button,
                {
                  shape: "text",
                  className: "p-0 underline",
                  as: Link,
                  href: `/${locale}/favorites`,
                  children: /* @__PURE__ */ jsx(Typography, { variant: "link-M", className: "inline" })
                }
              )
            ]
          }
        ) }),
        /* @__PURE__ */ jsx(
          Button,
          {
            shape: "text",
            className: "text-text p-0",
            onClick: () => {
              var _a2;
              undoRemoveFavorite(slug, title);
              (_a2 = toastRef.current) == null ? void 0 : _a2.dismiss(to.id);
            },
            children: t("calendar_common:favorites.undo")
          }
        )
      ] }),
      dismissible: false
    }));
    timeoutRef.current[slug] = setTimeout(() => {
      delete timeoutRef.current[slug];
    }, 5e3);
  };
  const undoRemoveFavorite = (slug, title) => {
    var _a;
    clearTimeout(timeoutRef.current[slug]);
    delete timeoutRef.current[slug];
    const updatedFavorites = [.../* @__PURE__ */ new Set([...favorites, slug])];
    updateFavorites(updatedFavorites);
    (_a = toastRef.current) == null ? void 0 : _a.publish((to) => ({
      title: /* @__PURE__ */ jsxs("div", { className: "bg-body flex items-center gap-2 rounded-md p-4", children: [
        /* @__PURE__ */ jsx("div", { className: "flex-1", children: /* @__PURE__ */ jsxs(
          Trans,
          {
            i18nKey: "favorites.event_restored_to_favorites",
            ns: "calendar_common",
            values: { title },
            children: [
              /* @__PURE__ */ jsx(Typography, { className: "inline", strong: true }),
              /* @__PURE__ */ jsx(
                Button,
                {
                  shape: "text",
                  className: "p-0 underline",
                  as: Link,
                  href: `/${locale}/favorites`,
                  children: /* @__PURE__ */ jsx(Typography, { variant: "link-M", className: "inline" })
                }
              )
            ]
          }
        ) }),
        /* @__PURE__ */ jsx(
          Button,
          {
            shape: "text",
            className: "text-text p-0",
            onClick: () => {
              var _a2;
              (_a2 = toastRef.current) == null ? void 0 : _a2.dismiss(to.id);
            },
            children: /* @__PURE__ */ jsx(CloseIcon, {})
          }
        )
      ] }),
      dismissible: false
    }));
  };
  const toggleFavorite = (slug, title) => {
    var _a;
    if (favorites.includes(slug)) {
      removeFavoriteWithUndo(slug, title);
    } else {
      const updatedFavorites = [...favorites, slug];
      updateFavorites(updatedFavorites);
      (_a = toastRef.current) == null ? void 0 : _a.publish((to) => ({
        title: /* @__PURE__ */ jsxs("div", { className: "bg-body flex items-center gap-2 rounded-md p-4", children: [
          /* @__PURE__ */ jsx("div", { className: "flex-1", children: /* @__PURE__ */ jsxs(
            Trans,
            {
              i18nKey: "favorites.event_added_to_favorites",
              ns: "calendar_common",
              values: { title },
              children: [
                /* @__PURE__ */ jsx(Typography, { className: "inline", strong: true }),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    shape: "text",
                    className: "p-0 underline",
                    as: Link,
                    href: `/${locale}/favorites`,
                    children: /* @__PURE__ */ jsx(Typography, { variant: "link-M", className: "inline" })
                  }
                )
              ]
            }
          ) }),
          /* @__PURE__ */ jsx(
            Button,
            {
              shape: "text",
              className: "text-text p-0",
              onClick: () => {
                var _a2;
                (_a2 = toastRef.current) == null ? void 0 : _a2.dismiss(to.id);
              },
              children: /* @__PURE__ */ jsx(CloseIcon, {})
            }
          )
        ] }),
        dismissible: false
      }));
    }
  };
  const isFavorite = (slug) => favorites.includes(slug);
  return /* @__PURE__ */ jsxs(context_default2.Provider, { value: { favorites, toggleFavorite, isFavorite, initialSlugs }, children: [
    /* @__PURE__ */ jsx(Toast, { ref: toastRef, className: "border-paper overflow-hidden rounded-xl border p-0" }),
    children
  ] });
};
var provider_default = FavoritesProvider;

// src/context/config/provider.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var ConfigProvider = ({ children, city }) => {
  const { locale } = useRouter2();
  const { data, isLoading } = useWebsiteConfig({ lang: locale, where: { slug: "config" } });
  return /* @__PURE__ */ jsx2(context_default.Provider, { value: __spreadValues({ config: data, city, isLoading }, CalendarConfig.config), children: /* @__PURE__ */ jsx2(provider_default, { children }) });
};
var provider_default2 = ConfigProvider;

// src/context/root/provider.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: false
    },
    mutations: {
      retry: 0
    }
  }
});
var CalendarProvider = (_a) => {
  var _b = _a, { children } = _b, props = __objRest(_b, ["children"]);
  return /* @__PURE__ */ jsxs2(QueryClientProvider, { client: queryClient, children: [
    /* @__PURE__ */ jsx3(provider_default2, __spreadProps(__spreadValues({}, props), { children })),
    /* @__PURE__ */ jsx3(ReactQueryDevtools, {})
  ] });
};

export {
  CalendarProvider
};
//# sourceMappingURL=chunk-UDZ42Z65.mjs.map